import { navigateTo } from 'nuxt/app'
import { triggerBrowserMessage } from '~/app.vue'
import { getToken, signOutUser } from '~/composables/useFirebase'
import { NuxtRoutes } from '~/constants/routes'
import { useImpersonateStore } from '~/stores/impersonate'

export const MFA_ENROLLMENT_REQUIRED = 'MFA_ENROLLMENT_REQUIRED'
export const MFA_VERIFICATION_REQUIRED = 'MFA_VERIFICATION_REQUIRED'

/** Marks a request so the temporary raw-fetch interceptor skips impersonation. */
export const IMPERSONATION_OPT_OUT_HEADER = 'X-Qutee-Skip-Impersonation'
export const IMPERSONATION_SESSION_HEADER = 'X-Impersonation-Session'

const mfaExemptPathPattern = /\/mfa(\/|$)/

const resolveRequestUrl = (input: RequestInfo | URL): string => {
  if (input instanceof Request) {
    return input.url
  }
  return input.toString()
}

export const isBackendRequest = (requestUrl: string, backendBasePath: string) => {
  if (!backendBasePath) {
    return false
  }

  if (backendBasePath.startsWith('/')) {
    try {
      const parsed = new URL(requestUrl, window.location.origin)
      return parsed.pathname.startsWith(backendBasePath)
    }
    catch {
      return requestUrl.includes(backendBasePath)
    }
  }

  return requestUrl.startsWith(backendBasePath)
}

export const extractApiErrorCode = (body: unknown): string | null => {
  if (!body || typeof body !== 'object') {
    return null
  }

  const record = body as Record<string, unknown>
  if (typeof record.error_code === 'string') {
    return record.error_code
  }

  const detail = record.detail
  if (detail && typeof detail === 'object') {
    const detailRecord = detail as Record<string, unknown>
    if (typeof detailRecord.error_code === 'string') {
      return detailRecord.error_code
    }
  }

  return null
}

export const shouldRedirectForMfaEnrollment = (
  requestUrl: string,
  backendBasePath: string,
  status: number,
  errorCode: string | null,
) =>
  status === 403
  && errorCode === MFA_ENROLLMENT_REQUIRED
  && isBackendRequest(requestUrl, backendBasePath)
  && !mfaExemptPathPattern.test(new URL(requestUrl, window.location.origin).pathname)

let mfaEnrollmentRedirectInFlight = false
let lastImpersonationExpiryNotifyAt = 0

const isOnMfaSetupPage = () => {
  if (!import.meta.client) {
    return false
  }
  return window.location.pathname.replace(/\/$/, '').endsWith('/auth/mfa-setup')
}

export const redirectToMfaSetup = async (
  message?: string,
) => {
  if (mfaEnrollmentRedirectInFlight || isOnMfaSetupPage()) {
    return
  }

  mfaEnrollmentRedirectInFlight = true
  try {
    if (message) {
      triggerBrowserMessage(message)
    }

    await navigateTo({ name: NuxtRoutes.AuthMfaSetup })
  }
  finally {
    mfaEnrollmentRedirectInFlight = false
  }
}

const redirectForMfaVerification = async () => {
  // Dynamic import avoids a cycle: backendFetch -> useMfa -> api/client -> backendFetch.
  const { hasPendingEmailMfaChallenge } = await import('~/composables/useMfa')
  if (hasPendingEmailMfaChallenge()) {
    await navigateTo({ name: NuxtRoutes.AuthMfaChallenge })
    return
  }

  await signOutUser()
  await navigateTo({ name: NuxtRoutes.AuthLogin })
}

/**
 * Shared MFA enrollment/verification response handling for both the typed
 * openapi-fetch client and the temporary raw-fetch interceptor.
 */
export const handleMfaEnrollmentRequiredResponse = async (
  requestUrl: string,
  response: Response,
  backendBasePath: string,
  enrollmentMessage?: string,
) => {
  if (response.status !== 403 || !isBackendRequest(requestUrl, backendBasePath)) {
    return
  }

  if (mfaExemptPathPattern.test(new URL(requestUrl, window.location.origin).pathname)) {
    return
  }

  let errorCode: string | null
  try {
    const body = await response.clone().json()
    errorCode = extractApiErrorCode(body)
  }
  catch {
    return
  }

  if (shouldRedirectForMfaEnrollment(requestUrl, backendBasePath, response.status, errorCode)) {
    await redirectToMfaSetup(enrollmentMessage)
    return
  }

  if (errorCode === MFA_VERIFICATION_REQUIRED) {
    await redirectForMfaVerification()
  }
}

export type ImpersonationHeaderApplication = {
  expiredCleared: boolean
  optedOut: boolean
}

/**
 * Mutates headers for the active backend impersonation session (or opt-out).
 * Shared by openapi-fetch middleware and the temporary raw-fetch plugin so both
 * paths cannot drift.
 */
export const applyImpersonationSessionHeaders = (
  headers: Headers,
): ImpersonationHeaderApplication => {
  if (headers.has(IMPERSONATION_OPT_OUT_HEADER)) {
    headers.delete(IMPERSONATION_OPT_OUT_HEADER)
    headers.delete(IMPERSONATION_SESSION_HEADER)
    return { expiredCleared: false, optedOut: true }
  }

  const impersonateStore = useImpersonateStore()
  const expiredCleared = impersonateStore.clearExpiredImpersonation()

  if (impersonateStore.isImpersonating && impersonateStore.impersonationSessionId) {
    headers.set(IMPERSONATION_SESSION_HEADER, impersonateStore.impersonationSessionId)
  }
  else {
    headers.delete(IMPERSONATION_SESSION_HEADER)
  }

  return { expiredCleared, optedOut: false }
}

/**
 * Debounced UI notice when an expired impersonation session is cleared.
 * Safe to call from both request pipelines on the same underlying fetch.
 */
export const notifyImpersonationSessionExpired = (
  expiredCleared: boolean,
  message?: string,
) => {
  if (!expiredCleared || !import.meta.client) {
    return
  }

  const now = Date.now()
  if (now - lastImpersonationExpiryNotifyAt < 1000) {
    return
  }
  lastImpersonationExpiryNotifyAt = now

  triggerBrowserMessage(
    message
    ?? 'The impersonation session expired. You are back in your own customer account.',
  )
}

/** Applies `Authorization: Bearer <firebase-id-token>`. Returns false when no token. */
export const applyFirebaseAuthHeader = async (headers: Headers): Promise<boolean> => {
  const token = await getToken()
  if (!token) {
    return false
  }
  headers.set('Authorization', `Bearer ${token}`)
  return true
}

/** Snapshot of auth headers for callers that build RequestInit manually. */
export const getCurrentAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getToken()
  if (!token) {
    return {}
  }
  return { Authorization: `Bearer ${token}` }
}

export const wrapFetchWithBackendInterceptors = (
  baseFetch: typeof fetch,
  backendBasePath: string,
  enrollmentMessage?: string,
): typeof fetch => {
  return async (input, init) => {
    const requestUrl = resolveRequestUrl(input)
    const response = await baseFetch(input, init)
    await handleMfaEnrollmentRequiredResponse(
      requestUrl,
      response,
      backendBasePath,
      enrollmentMessage,
    )
    return response
  }
}
